import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Loader2, Star, Users } from "lucide-react";
import StaffHeader from "@/components/StaffHeader";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import CaseImage from "@/components/CaseImage";
import DonationProgress from "@/components/DonationProgress";
import api, { getCasePhotoUrls } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const STATUS_BADGE = {
  active: { label: "Active", className: "bg-amtm-info/15 text-amtm-info" },
  funded: { label: "Funded", className: "bg-amtm-success/15 text-amtm-success" },
  closed: { label: "Closed", className: "bg-amtm-textMuted/15 text-amtm-textMuted" },
};

export default function Fundraisers() {
  const [cases, setCases] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const { toast } = useToast();

  const fetchCases = useCallback(async () => {
    const { data } = await api.get("/cases", { params: { fundraising_only: true, limit: 100 } });
    setCases(data.items);
  }, []);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  const toggleFeatured = async (c) => {
    setSavingId(c.id);
    try {
      await api.patch(`/cases/${c.id}`, { featured: !c.featured });
      setCases((prev) => prev.map((item) => (item.id === c.id ? { ...item, featured: !c.featured } : item)));
      toast({ title: !c.featured ? "Marked as featured" : "Removed from featured" });
    } catch {
      toast({ variant: "destructive", title: "Could not update" });
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-amtm-bg" data-testid="admin-fundraisers-page">
      <StaffHeader />
      <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
        <div className="mb-6">
          <h1 className="font-display text-3xl text-amtm-text" data-testid="fundraisers-page-title">Fundraisers</h1>
          <p className="text-sm text-amtm-textMuted mt-1">
            Every public case with a donation goal. Feature up to 2-3 on the homepage's "Rescues that need you now" section.
          </p>
        </div>

        {cases === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amtm-teal" /></div>
        ) : cases.length === 0 ? (
          <p className="text-amtm-textMuted text-center py-12" data-testid="fundraisers-empty-state">
            No cases have a donation goal yet. Set a Goal Amount on a case's Donation Needed field to start a fundraiser.
          </p>
        ) : (
          <div className="space-y-3" data-testid="fundraisers-list">
            {cases.map((c) => {
              const badge = STATUS_BADGE[c.fundraising_status] || STATUS_BADGE.closed;
              return (
                <div key={c.id} className="bg-amtm-surface border border-amtm-border rounded-2xl p-4 flex gap-4 items-center" data-testid={`fundraiser-row-${c.slug}`}>
                  <Link to={`/cases/${c.id}`} className="h-16 w-16 rounded-xl overflow-hidden shrink-0 bg-amtm-give/30">
                    <CaseImage src={getCasePhotoUrls(c)[0]} alt={c.animal_name} animalType={c.animal_type} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/cases/${c.id}`} className="font-semibold text-amtm-text hover:underline" data-testid={`fundraiser-name-${c.slug}`}>
                        #{c.case_number} {c.animal_name}
                      </Link>
                      <Badge className={badge.className} data-testid={`fundraiser-status-${c.slug}`}>{badge.label}</Badge>
                      {!c.is_public && <Badge variant="outline">Not Public</Badge>}
                    </div>
                    <div className="mt-2 max-w-sm">
                      <DonationProgress
                        needed={c.donation_needed}
                        received={c.donation_received_total ?? c.donation_received}
                        donorCount={c.donor_count}
                        testId={`fundraiser-progress-${c.slug}`}
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    <label className="flex items-center gap-2 text-xs text-amtm-textMuted cursor-pointer" data-testid={`fundraiser-featured-label-${c.slug}`}>
                      <Star className="h-3.5 w-3.5" /> Featured
                      <Switch
                        checked={!!c.featured}
                        disabled={savingId === c.id}
                        onCheckedChange={() => toggleFeatured(c)}
                        data-testid={`fundraiser-featured-toggle-${c.slug}`}
                      />
                    </label>
                    <span className="flex items-center gap-1 text-xs text-amtm-textMuted" data-testid={`fundraiser-donor-count-${c.slug}`}>
                      <Users className="h-3.5 w-3.5" /> {c.donor_count || 0} donors
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
